import React, { useEffect, useRef, useState } from 'react';

// Campo de fecha que siempre se ve como día/mes/año. El <input type="date">
// nativo muestra el formato del idioma del navegador (en un Chrome en inglés,
// mes/día/año) y no se puede cambiar con lang. Este campo se escribe dd/mm/aaaa
// y el botón abre el calendario nativo. Recibe y devuelve la fecha en formato
// AAAA-MM-DD, igual que el input nativo, así se reemplaza sin tocar el resto.

const aTexto = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

// Devuelve la fecha AAAA-MM-DD si el texto es una fecha real completa, '' si
// está vacío y null si está incompleto o no existe (ej. 31/02/2026).
export function textoAIso(texto) {
  const limpio = String(texto || '').trim();
  if (!limpio) return '';
  const iso = limpio.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const local = limpio.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const [anio, mes, dia] = iso ? [iso[1], iso[2], iso[3]] : local ? [local[3], local[2], local[1]] : [];
  if (!anio) return null;
  const fecha = new Date(Date.UTC(Number(anio), Number(mes) - 1, Number(dia)));
  if (fecha.getUTCFullYear() !== Number(anio) || fecha.getUTCMonth() !== Number(mes) - 1 || fecha.getUTCDate() !== Number(dia)) return null;
  return `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

// Agrega las barras mientras se escribe, tecla por tecla o pegando todo junto:
// "01102026" → "01/10/2026". El día y el mes cortan solos a los 2 dígitos y
// también se aceptan barras escritas a mano ("1/10/2026").
const conBarras = (texto) => {
  if (/^\d{4}-/.test(texto)) return texto;
  const partes = [];
  let actual = '';
  for (const c of String(texto).replace(/[^\d/]/g, '')) {
    if (c === '/') {
      if (partes.length < 2 && actual) { partes.push(actual); actual = ''; }
      continue;
    }
    if (partes.length < 2 && actual.length === 2) { partes.push(actual); actual = ''; }
    if (partes.length === 2 && actual.length === 4) break;
    actual += c;
  }
  return [...partes, actual].join('/');
};

export default function InputFecha({ value, onChange, min, max, required, disabled, className = '', style, id, ...resto }) {
  const [texto, setTexto] = useState(aTexto(value));
  const campo = useRef(null);
  const calendario = useRef(null);

  // Si la fecha cambia desde afuera (calendario, flechas, carga), se actualiza el texto.
  useEffect(() => {
    setTexto((actual) => (textoAIso(actual) === (value || '') ? actual : aTexto(value)));
  }, [value]);

  const avisar = (iso) => onChange?.({ target: { value: iso }, currentTarget: { value: iso } });

  const validar = (iso) => {
    let mensaje = '';
    if (iso === null) mensaje = 'Escribí la fecha completa como día/mes/año (dd/mm/aaaa).';
    else if (iso && min && iso < min) mensaje = `La fecha tiene que ser desde el ${aTexto(min)}.`;
    else if (iso && max && iso > max) mensaje = `La fecha tiene que ser hasta el ${aTexto(max)}.`;
    campo.current?.setCustomValidity(mensaje);
  };

  useEffect(() => { validar(textoAIso(texto)); });

  const escribir = (e) => {
    const nuevo = conBarras(e.target.value);
    setTexto(nuevo);
    const iso = textoAIso(nuevo);
    if (iso !== null && iso !== (value || '')) avisar(iso);
  };

  return (
    <div className="relative" style={style ? { width: style.width } : undefined}>
      <input
        {...resto}
        ref={campo}
        id={id}
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        autoComplete="off"
        value={texto}
        onChange={escribir}
        onBlur={() => { if (textoAIso(texto) !== null) setTexto(aTexto(value)); }}
        required={required}
        disabled={disabled}
        className={`${className} pr-10`}
        style={style ? { ...style, width: '100%' } : undefined}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Abrir calendario"
        onClick={() => {
          try { calendario.current?.showPicker?.(); } catch { calendario.current?.focus(); }
        }}
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-500 hover:text-slate-800 disabled:opacity-40 dark:text-slate-400"
      >
        <span aria-hidden="true" className="material-symbols-outlined text-lg">calendar_month</span>
      </button>
      <input
        ref={calendario}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => { setTexto(aTexto(e.target.value)); avisar(e.target.value); }}
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
      />
    </div>
  );
}
